/**
 * The four numbers, in the order they happen.
 *
 * Written out flat, "205 455 matched" and "200 sendable" sit next to each other
 * as two facts and the 205 255 between them is left as an exercise. They are not
 * two facts, they are one chain: the filter matches a pile, one pass screens as
 * much of it as it is allowed to, the addresses in that pass collapse to the
 * distinct mailboxes behind them, and the day's cap decides how many of those
 * actually go out. Each step is a subset of the one above it, so it is drawn as
 * one, with the drop named on every rung.
 *
 * The bars are proportional to the FIRST step, not to the previous one. A bar
 * rescaled at every step would make each drop look the same size, which is
 * exactly the impression this card exists to prevent.
 */
import { cn } from "@wa/ui";
import { useT } from "../../../../../i18n/I18nProvider";
import type { AudiencePreview } from "../../../../../lib/marketing";
import { Bar, num, pct, type Tone } from "../../shared";

interface Step {
  id: string;
  label: string;
  value: number;
  tone: Tone;
  /** Why this step is smaller than the one above it. */
  note?: string;
}

function Row({ step, top, first }: { step: Step; top: number; first: boolean }) {
  return (
    <li className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className={cn("text-xs", first ? "font-medium" : "text-muted-foreground")}>{step.label}</span>
        <span className="flex items-baseline gap-2">
          {!first && <span className="font-mono text-[10px] text-muted-foreground/60">{pct(step.value, top)}</span>}
          <span
            className={cn(
              "font-mono text-sm tabular-nums",
              step.tone === "good" && "font-semibold text-emerald-600 dark:text-emerald-400",
              step.tone === "muted" && "text-muted-foreground",
            )}
          >
            {num(step.value)}
          </span>
        </span>
      </div>
      <Bar value={step.value} total={top} tone={step.tone} />
      {step.note && <p className="text-[10px] leading-snug text-muted-foreground/70">{step.note}</p>}
    </li>
  );
}

export function Funnel({ preview }: { preview: AudiencePreview }) {
  const t = useT();

  const steps: Step[] = [
    {
      id: "total",
      label: t(`marketing.preview.matched.${preview.unit}`),
      value: preview.total,
      tone: "neutral",
    },
  ];

  // The examined rung only earns its place when the pass stopped short. When
  // the whole audience fitted in one batch it would just repeat the row above.
  if (preview.partial) {
    steps.push({
      id: "examined",
      label: t("marketing.preview.step.examined"),
      value: preview.examined,
      tone: "muted",
      note: t("marketing.preview.step.examinedNote"),
    });
  }

  steps.push({
    id: "unique",
    label: t("marketing.preview.uniqueAddresses"),
    value: preview.uniqueAddresses,
    tone: "muted",
    note:
      preview.examined > preview.uniqueAddresses
        ? t("marketing.preview.step.uniqueNote", { n: num(preview.examined - preview.uniqueAddresses) })
        : undefined,
  });

  steps.push({
    id: "sendable",
    label: t("marketing.preview.sendable"),
    value: preview.sendable,
    tone: "good",
    note: t("marketing.preview.step.sendableNote"),
  });

  const top = Math.max(1, preview.total);

  return (
    <ol className="space-y-3">
      {steps.map((s, i) => (
        <Row key={s.id} step={s} top={top} first={i === 0} />
      ))}
    </ol>
  );
}
